import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';
import HamburgerMenu from './HamburgerMenu';
import { useAuth } from './useAuth';
import { clearAdsbFiltersCache, DEFAULT_REGISTRATION_PREFIXES, DEFAULT_TYPE_CODES } from './adsbExchange';

const getStyles = (theme) => {
  const isDark = theme === 'dark';
  const colors = {
    bg: isDark ? '#121212' : '#f4f7f6',
    cardBg: isDark ? '#1e1e1e' : '#fff',
    text: isDark ? '#e0e0e0' : '#333',
    subtleText: isDark ? '#a0a0a0' : '#666',
    border: isDark ? '#444' : '#ddd',
    shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    link: isDark ? '#6cb2f7' : '#007bff',
  };

  return {
    page: { backgroundColor: colors.bg, minHeight: '100vh', padding: '20px' },
    container: { maxWidth: '900px', margin: '0 auto', fontFamily: 'sans-serif' },
    pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    pageTitle: { fontSize: '24px', margin: 0, color: colors.text },
    card: {
      backgroundColor: colors.cardBg,
      borderRadius: '8px',
      padding: '20px',
      boxShadow: `0 2px 4px ${colors.shadow}`,
      marginBottom: '20px',
    },
    cardTitle: { fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', color: colors.text },
    formGroup: { marginBottom: '20px' },
    label: { display: 'block', marginBottom: '8px', fontWeight: 'bold', color: colors.text, fontSize: '14px' },
    input: {
      width: '100%',
      padding: '8px 12px',
      border: `1px solid ${colors.border}`,
      borderRadius: '4px',
      backgroundColor: isDark ? '#2a2a2a' : '#fff',
      color: colors.text,
      fontSize: '14px',
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      minHeight: '90px',
      padding: '8px 12px',
      border: `1px solid ${colors.border}`,
      borderRadius: '4px',
      backgroundColor: isDark ? '#2a2a2a' : '#fff',
      color: colors.text,
      fontSize: '14px',
      boxSizing: 'border-box',
      fontFamily: 'monospace',
    },
    button: {
      padding: '10px 20px',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 'bold',
      marginRight: '10px',
    },
    buttonPrimary: { backgroundColor: colors.link, color: 'white' },
    buttonSecondary: { backgroundColor: '#6c757d', color: 'white' },
    helpText: { fontSize: '12px', color: colors.subtleText, fontStyle: 'italic', marginTop: '4px' },
    checkboxRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' },
    statusBadge: (configured) => ({
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      backgroundColor: configured ? '#d4edda' : '#f8d7da',
      color: configured ? '#155724' : '#721c24',
    }),
  };
};

const linesToList = (value) =>
  String(value || '')
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean);

const listToLines = (value) => (Array.isArray(value) ? value.join('\n') : '');

function ADSBAdminPage({ theme }) {
  const styles = getStyles(theme);
  const [config, setConfig] = useState(null);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const navigate = useNavigate();
  const { isStaff } = useAuth();

  useEffect(() => {
    if (!isStaff) {
      setAlert({ show: true, message: 'Access denied. Staff access required.', type: 'error' });
      setTimeout(() => navigate('/'), 2000);
    }
  }, [isStaff, navigate]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        const response = await api.get('/adsb-configuration/');
        const configData = Array.isArray(response.data) ? response.data[0] : response.data;
        if (!configData) {
          setAlert({
            show: true,
            message: 'Configuration not found. Run database migrations and try again.',
            type: 'error',
          });
          return;
        }
        setConfig({
          ...configData,
          aircraft_type_codes_text: listToLines(configData.aircraft_type_codes),
          registration_prefixes_text: listToLines(configData.registration_prefixes),
        });
      } catch (err) {
        console.error('Error fetching ADSB configuration:', err);
        if (err.response?.status === 401) {
          navigate('/login');
        } else {
          setAlert({
            show: true,
            message: `Failed to load configuration: ${err.response?.data?.detail || err.message}`,
            type: 'error',
          });
        }
      } finally {
        setLoading(false);
      }
    };

    if (isStaff) {
      fetchConfig();
    }
  }, [isStaff, navigate]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        ...config,
        aircraft_type_codes: linesToList(config.aircraft_type_codes_text),
        registration_prefixes: linesToList(config.registration_prefixes_text),
      };
      if (apiKeyInput.trim()) {
        payload.rapidapi_key = apiKeyInput.trim();
      }
      delete payload.aircraft_type_codes_text;
      delete payload.registration_prefixes_text;
      delete payload.has_rapidapi_key;
      delete payload.rapidapi_key_masked;

      const configId = config.id || 1;
      await api.put(`/adsb-configuration/${configId}/`, payload);
      clearAdsbFiltersCache();

      const response = await api.get('/adsb-configuration/');
      const configData = Array.isArray(response.data) ? response.data[0] : response.data;
      setConfig({
        ...configData,
        aircraft_type_codes_text: listToLines(configData.aircraft_type_codes),
        registration_prefixes_text: listToLines(configData.registration_prefixes),
      });
      setApiKeyInput('');
      setAlert({ show: true, message: 'ADSB configuration saved successfully!', type: 'success' });
    } catch (err) {
      console.error('Error saving ADSB configuration:', err);
      setAlert({ show: true, message: 'Failed to save configuration. Please try again.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleResetFilters = () => {
    setConfig(prev => ({
      ...prev,
      aircraft_type_codes_text: listToLines(DEFAULT_TYPE_CODES),
      registration_prefixes_text: listToLines(DEFAULT_REGISTRATION_PREFIXES),
      adsb_exchange_poll_interval_ms: 2000,
    }));
  };

  if (!isStaff) {
    return (
      <div style={styles.page}>
        <CustomAlert {...alert} onClose={() => setAlert({ show: false, message: '', type: '' })} theme={theme} />
      </div>
    );
  }

  if (loading || !config) {
    return (
      <div style={styles.page}>
        <Spinner />
      </div>
    );
  }

  const displayConfig = {
    ...config,
    aircraft_type_codes_text: config.aircraft_type_codes_text ?? listToLines(config.aircraft_type_codes),
    registration_prefixes_text: config.registration_prefixes_text ?? listToLines(config.registration_prefixes),
  };

  return (
    <div style={styles.page}>
      <CustomAlert {...alert} onClose={() => setAlert({ show: false, message: '', type: '' })} theme={theme} />
      <div style={styles.container}>
        <div style={styles.pageHeader}>
          <h1 style={styles.pageTitle}>ADSB Map Admin</h1>
          <HamburgerMenu theme={theme} />
        </div>

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>RapidAPI / ADSB Exchange</h2>
          <p style={styles.helpText}>
            Configure the RapidAPI key for this server. The key is stored on the backend and never sent to browsers.
            Each deployment should use its own key so API usage is billed to that server&apos;s account.
          </p>

          <div style={styles.formGroup}>
            <span style={styles.statusBadge(config.has_rapidapi_key)}>
              {config.has_rapidapi_key ? 'API key configured' : 'No API key configured'}
            </span>
            {config.rapidapi_key_masked && (
              <div style={{ ...styles.helpText, marginTop: '8px' }}>
                Current key: {config.rapidapi_key_masked}
              </div>
            )}
          </div>

          <div style={styles.checkboxRow}>
            <input
              id="rapidapi-enabled"
              type="checkbox"
              checked={displayConfig.rapidapi_enabled}
              onChange={(e) => setConfig(prev => ({ ...prev, rapidapi_enabled: e.target.checked }))}
            />
            <label htmlFor="rapidapi-enabled" style={{ ...styles.label, marginBottom: 0 }}>
              Enable ADSB Exchange data
            </label>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>RapidAPI Key</label>
            <input
              type="password"
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              placeholder={config.has_rapidapi_key ? 'Leave blank to keep current key' : 'Enter RapidAPI key'}
              style={styles.input}
              autoComplete="off"
            />
            <div style={styles.helpText}>From your RapidAPI ADSBExchange subscription. Only enter when adding or changing the key.</div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>RapidAPI Host</label>
            <input
              type="text"
              value={displayConfig.rapidapi_host || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, rapidapi_host: e.target.value }))}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>API Base URL</label>
            <input
              type="text"
              value={displayConfig.rapidapi_base_url || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, rapidapi_base_url: e.target.value }))}
              style={styles.input}
            />
            <div style={styles.helpText}>Default military feed: https://adsbexchange-com1.p.rapidapi.com/v2/mil</div>
          </div>
        </div>

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Aircraft Filters</h2>
          <p style={styles.helpText}>
            These filters are applied server-side when fetching ADSB Exchange data for the map and status board.
          </p>

          <div style={styles.formGroup}>
            <label style={styles.label}>Aircraft Type Codes (one per line)</label>
            <textarea
              value={displayConfig.aircraft_type_codes_text}
              onChange={(e) => setConfig(prev => ({ ...prev, aircraft_type_codes_text: e.target.value }))}
              style={styles.textarea}
            />
            <div style={styles.helpText}>Matches exact codes or substrings (ICAO type designators).</div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Registration Prefixes (one per line)</label>
            <textarea
              value={displayConfig.registration_prefixes_text}
              onChange={(e) => setConfig(prev => ({ ...prev, registration_prefixes_text: e.target.value }))}
              style={styles.textarea}
            />
            <div style={styles.helpText}>Only aircraft whose registration/callsign starts with these prefixes are shown.</div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Poll Interval (milliseconds)</label>
            <input
              type="number"
              min="1000"
              step="500"
              value={displayConfig.adsb_exchange_poll_interval_ms || 2000}
              onChange={(e) => setConfig(prev => ({
                ...prev,
                adsb_exchange_poll_interval_ms: parseInt(e.target.value, 10) || 2000,
              }))}
              style={{ ...styles.input, maxWidth: '200px' }}
            />
            <div style={styles.helpText}>How often the map refreshes ADSB Exchange data (default: 2000 ms).</div>
          </div>
        </div>

        <div>
          <button
            type="button"
            style={{ ...styles.button, ...styles.buttonPrimary }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
          <button
            type="button"
            style={{ ...styles.button, ...styles.buttonSecondary }}
            onClick={handleResetFilters}
            disabled={saving}
          >
            Reset Filters to Defaults
          </button>
        </div>
      </div>
    </div>
  );
}

export default ADSBAdminPage;
